import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

export default function PaceNudges() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/pace/nudges");
      setItems(data.items || []);
    } catch (_e) {
      // Silent-empty on error — Pace should never break the dashboard.
      setItems([]);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const dismiss = async (id) => {
    setItems((prev) => prev.filter((n) => n.id !== id));
    try {
      await api.post(`/pace/nudges/${id}/dismiss`);
    } catch (_e) {
      // Rollback on error
      load();
    }
  };

  // Silent-empty on: no nudges, still loading, disabled tenant, or errored
  if (!loaded || items.length === 0) return null;

  return (
    <section aria-label="Pace suggestions" data-testid="pace-nudges" className="space-y-3">
      <div className="flex items-center gap-2">
        <img
          src="/brand/pace/pace-avatar-64.png"
          alt=""
          className="h-6 w-6 rounded-full ring-1 ring-secondary/40"
        />
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-secondary">
          From Pace
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {items.slice(0, 3).map((n) => (
          <PaceNudgeCard key={n.id} nudge={n} onDismiss={() => dismiss(n.id)} />
        ))}
      </div>
    </section>
  );
}

function PaceNudgeCard({ nudge, onDismiss }) {
  return (
    <article
      data-testid={`pace-nudge-${nudge.kind}`}
      className="relative flex flex-col rounded-2xl border border-border bg-card p-5 shadow-[0_6px_22px_rgba(11,37,69,0.05)] transition-colors hover:border-secondary/40"
    >
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onDismiss}
        data-testid={`pace-dismiss-${nudge.kind}`}
        className="absolute right-3 top-3 rounded-full p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-primary"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="mb-3 flex items-start gap-3">
        <img
          src="/brand/pace/pace-avatar-128.png"
          alt="Pace"
          width="48"
          height="48"
          className="h-12 w-12 flex-shrink-0 rounded-full ring-1 ring-secondary/40"
        />
        <div className="min-w-0">
          <h3 className="font-display text-base font-semibold leading-snug text-primary">
            {nudge.title}
          </h3>
        </div>
      </div>
      <p className="mb-4 flex-1 text-sm leading-relaxed text-muted-foreground">
        {nudge.body}
      </p>
      <div className="flex items-center justify-between gap-2">
        <Button asChild size="sm" data-testid={`pace-cta-${nudge.kind}`}>
          <Link to={nudge.primary_cta?.deep_link || "/app/dashboard"}>
            {nudge.primary_cta?.label || "Open"}
          </Link>
        </Button>
        {nudge.rendered_by === "fallback" ? (
          <span
            className="text-[10px] uppercase tracking-widest text-muted-foreground/70"
            title="Rendered from a hardcoded template because the LLM was unavailable"
          >
            template
          </span>
        ) : (
          <span className="text-[10px] uppercase tracking-widest text-secondary/80">
            ai
          </span>
        )}
      </div>
    </article>
  );
}
